/**
 * SHARING A HOARD'S COINS AND GEMS (qa-runs 1156). Pure: the Loot tool's preview and the server's give both read it, so
 * what the DM is shown is what is given.
 *
 * - A split is by weights: one person (one weight), percentages (must total 100) or evenly (every weight 1).
 * - The hoard's worth in copper is split by weight and each share paid in its own coins, largest first; a coin breaks
 *   into the next smaller one only where a share needs it (2 sp over four people is 20 cp, 5 cp each); the copper that
 *   will not divide goes to the person the DM picks. A coin outside the ladder is split whole, its rest to the
 *   same person.
 * - Gems are never split: each goes whole to one person or stays held.
 * - Whatever is not given stays held: the hoard less what was given, borrowing from the next larger coin when a share
 *   took coins the hoard held only as larger ones.
 */
/** The coins in copper, largest first: the system's own (platinum, gold, silver, copper in 3.5 and Pathfinder). */
export const COIN_LADDER = [
    { coin: 'pp', cp: 1000 }, { coin: 'gp', cp: 100 }, { coin: 'sp', cp: 10 }, { coin: 'cp', cp: 1 },
];
const STEP = new Map(COIN_LADDER.map((c, i) => [c.coin, i]));
const whole = (n) => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
/** Percentages are whole numbers, two or more people, and add up to exactly 100. */
export function percentagesValid(shares) {
    if (shares.length < 2)
        return false;
    if (!shares.every((s) => Number.isInteger(s.percent) && s.percent >= 0 && s.percent <= 100))
        return false;
    return shares.reduce((n, s) => n + s.percent, 0) === 100;
}
/**
 * Each person's coins by weight (qa-runs 1196): the hoard's worth in copper is split by weight, the copper that will not
 * divide going to `restTo` (one of the ids); each share is then paid in the hoard's own coins, largest first, a coin
 * broken into the next smaller one only where a share needs it, never more of a coin than is left to give.
 */
export function splitCoins(coins, people, restTo) {
    const out = {};
    const who = people.filter((p) => p.weight > 0);
    for (const p of who)
        out[p.id] = {};
    if (!who.length)
        return out;
    const total = who.reduce((n, p) => n + p.weight, 0);
    const add = (id, coin, n) => { if (n > 0)
        out[id][coin] = (out[id][coin] ?? 0) + n; };
    const rest = out[restTo] ? restTo : who[0].id;
    const pool = COIN_LADDER.map(({ coin }) => whole(coins[coin]));
    const worth = pool.reduce((n, c, i) => n + c * COIN_LADDER[i].cp, 0);
    const due = new Map(who.map((p) => [p.id, Math.floor((worth * p.weight) / total)]));
    due.set(rest, due.get(rest) + worth - [...due.values()].reduce((n, v) => n + v, 0));
    const below = (k) => pool.reduce((n, c, i) => (i > k ? n + c * COIN_LADDER[i].cp : n), 0);
    for (const p of who) {
        let owed = due.get(p.id);
        let k = 0;
        while (owed > 0 && k < COIN_LADDER.length) {
            const { coin, cp } = COIN_LADDER[k];
            const n = Math.min(Math.floor(owed / cp), pool[k]);
            add(p.id, coin, n);
            pool[k] = pool[k] - n;
            owed -= n * cp;
            if (owed <= below(k)) {
                k += 1;
                continue;
            }
            // The smaller coins left can't make up the rest: break the nearest larger-or-equal coin still held.
            let j = k;
            while (j >= 0 && pool[j] === 0)
                j -= 1;
            if (j < 0 || j === COIN_LADDER.length - 1)
                break; // cannot happen: the shares add up to the hoard's worth
            pool[j] = pool[j] - 1;
            pool[j + 1] = pool[j + 1] + COIN_LADDER[j].cp / COIN_LADDER[j + 1].cp;
            k = j + 1;
        }
    }
    for (const [coin, n] of Object.entries(coins)) {
        if (STEP.has(coin))
            continue;
        let given = 0;
        for (const p of who) {
            const m = Math.floor((whole(n) * p.weight) / total);
            add(p.id, coin, m);
            given += m;
        }
        add(rest, coin, whole(n) - given);
    }
    return out;
}
/** What stays held: the hoard less every share, a short coin borrowed from the next larger one. Never negative. */
export function heldAfter(hoard, given) {
    const out = {};
    for (const [c, n] of Object.entries(leftAfter(hoard, given)))
        if (n > 0)
            out[c] = n;
    return out;
}
/** The hoard less every share, borrowing down the ladder; a coin below zero means the shares took more than it holds. */
function leftAfter(hoard, given) {
    const left = {};
    for (const [c, n] of Object.entries(hoard))
        left[c] = whole(n);
    for (const g of given)
        for (const [c, n] of Object.entries(g))
            left[c] = (left[c] ?? 0) - whole(n);
    for (let i = COIN_LADDER.length - 1; i > 0; i -= 1) {
        const { coin, cp } = COIN_LADDER[i];
        const up = COIN_LADDER[i - 1];
        const short = left[coin] ?? 0;
        if (short >= 0)
            continue;
        const per = up.cp / cp;
        const borrow = Math.ceil(-short / per);
        left[coin] = short + borrow * per;
        left[up.coin] = (left[up.coin] ?? 0) - borrow;
    }
    return left;
}
/** Whether every share fits in the hoard: no coin given that the hoard does not hold, itself or as larger coins broken down. */
export function fitsHoard(hoard, given) {
    return Object.values(leftAfter(hoard, given)).every((n) => n >= 0);
}
/** "300 gp, 7 sp, 5 cp and a Bloodstone": a share as its player is told it. */
export function describeShare(coins, gems = []) {
    const order = (c) => STEP.get(c) ?? COIN_LADDER.length;
    const parts = Object.entries(coins).filter(([, n]) => whole(n) > 0).sort(([a], [b]) => order(a) - order(b))
        .map(([c, n]) => `${whole(n).toLocaleString('en-US')} ${c}`);
    for (const g of gems)
        parts.push(`${/^[aeiou]/i.test(g) ? 'an' : 'a'} ${g}`);
    if (parts.length <= 1)
        return parts.join('');
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
//# sourceMappingURL=hoardSplit.js.map